import { useState, useEffect, useRef } from 'react';
import { ChevronDown, Building2, Check } from 'lucide-react';
import { useCuentas } from '../../graphql/tesoreria';

const bankName = (cuenta) => typeof cuenta?.banco === 'string'
  ? cuenta.banco
  : cuenta?.banco?.nombre || cuenta?.banco_nombre || '';
const bankCode = (cuenta) => cuenta?.banco?.codigo || cuenta?.banco_codigo || cuenta?.codigo_banco || cuenta?.bancoCodigo || '';
const bankLabel = (cuenta) => `${bankCode(cuenta) ? `${bankCode(cuenta)} · ` : ''}${bankName(cuenta)}`;

/**
 * SelectorCuenta - Select con búsqueda para cuentas bancarias activas
 * 
 * @param {Object} props
 * @param {string} props.value - ID de la cuenta seleccionada
 * @param {Function} props.onChange - Callback al cambiar (recibe el objeto cuenta completo)
 * @param {string} props.label - Label del campo
 * @param {string} props.placeholder - Placeholder
 * @param {boolean} props.required - Si el campo es obligatorio
 * @param {string} props.error - Mensaje de error
 * @param {string} props.monedaFiltro - Filtrar por moneda específica
 * @param {boolean} props.disabled - Deshabilitar el selector
 */
export default function SelectorCuenta({
  value,
  onChange,
  label = 'Cuenta Bancaria',
  placeholder = 'Seleccionar cuenta',
  required = false,
  error = '',
  monedaFiltro = null,
  disabled = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [filteredCuentas, setFilteredCuentas] = useState([]);
  const containerRef = useRef(null);

  // TODO: Usar el hook real cuando esté conectado el backend
  const [result] = useCuentas({ estado: 'ACTIVO', moneda: monedaFiltro });
  const cuentas = result.data?.cuentasBancarias?.data || [];

  const selectedCuenta = cuentas.find((c) => String(c.id) === String(value));

  useEffect(() => {
    if (search) {
      const query = search.toLowerCase();
      const filtered = cuentas.filter((cuenta) =>
        `${bankLabel(cuenta)} ${cuenta?.descripcion || ''} ${cuenta?.cbu || ''} ${cuenta?.alias || ''}`.toLowerCase().includes(query)
      );
      setFilteredCuentas(filtered);
    } else {
      setFilteredCuentas(cuentas);
    }
  }, [search, cuentas]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (cuenta) => {
    onChange(cuenta);
    setIsOpen(false);
    setSearch('');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="relative" ref={containerRef}>
      {label && (
        <label className="block text-sm font-medium text-[rgb(var(--fg))] mb-1.5">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl border bg-[rgba(var(--bg))] text-left transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))] ${
          error
            ? 'border-red-500 focus:border-red-500'
            : 'border-[rgb(var(--border))] focus:border-[rgb(var(--brand))]'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-[rgb(var(--brand))]'}`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-invalid={!!error}
      >
        {selectedCuenta ? (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Building2 className="w-4 h-4 text-[rgb(var(--muted))] flex-shrink-0" aria-hidden="true" />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-[rgb(var(--fg))] truncate">
                {bankLabel(selectedCuenta)}
              </div>
              <div className="text-xs text-[rgb(var(--muted))] truncate">
                {selectedCuenta.descripcion} · {selectedCuenta.moneda}
              </div>
            </div>
          </div>
        ) : (
          <span className="text-[rgb(var(--muted))]">{placeholder}</span>
        )}
        <ChevronDown
          className={`w-5 h-5 text-[rgb(var(--muted))] transition-transform flex-shrink-0 ${
            isOpen ? 'transform rotate-180' : ''
          }`}
          aria-hidden="true"
        />
      </button>

      {error && (
        <p className="mt-1 text-sm text-red-500" role="alert">
          {error}
        </p>
      )}

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--panel))] shadow-[var(--shadow)] overflow-hidden">
          {/* Search */}
          <div className="p-2 border-b border-[rgb(var(--border))]">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar cuenta..."
              className="w-full px-3 py-2 rounded-lg border border-[rgb(var(--border))] bg-[rgba(var(--bg))] text-[rgb(var(--fg))] text-sm placeholder:text-[rgb(var(--muted))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              aria-label="Buscar cuenta"
              autoFocus
            />
          </div>

          {/* List */}
          <div className="max-h-64 overflow-y-auto" role="listbox">
            {filteredCuentas.length > 0 ? (
              filteredCuentas.map((cuenta) => (
                <button
                  key={cuenta.id}
                  type="button"
                  onClick={() => handleSelect(cuenta)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[rgba(var(--muted),0.3)] transition-colors ${
                    value === cuenta.id ? 'bg-[rgba(var(--brand),0.1)]' : ''
                  }`}
                  role="option"
                  aria-selected={value === cuenta.id}
                >
                  <Building2
                    className="w-5 h-5 text-[rgb(var(--muted))] flex-shrink-0"
                    aria-hidden="true"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-[rgb(var(--fg))]">
                        {bankLabel(cuenta)}
                      </span>
                      <span className="text-xs px-1.5 py-0.5 rounded bg-[rgba(var(--brand),0.2)] text-[rgb(var(--brand))]">
                        {cuenta.moneda}
                      </span>
                    </div>
                    <div className="text-sm text-[rgb(var(--muted))] truncate">
                      {cuenta.descripcion}
                    </div>
                    <div className="text-xs text-[rgb(var(--muted))] mt-0.5">
                      {cuenta.alias || cuenta.cbu}
                    </div>
                    <div className="text-xs text-[rgb(var(--fg))] mt-0.5 font-medium">
                      Saldo: {formatCurrency(cuenta.saldo)}
                    </div>
                  </div>
                  {value === cuenta.id && (
                    <Check
                      className="w-5 h-5 text-[rgb(var(--brand))] flex-shrink-0"
                      aria-hidden="true"
                    />
                  )}
                </button>
              ))
            ) : (
              <div className="px-4 py-8 text-center text-[rgb(var(--muted))] text-sm">
                No se encontraron cuentas
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
